import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel, ExpandMore } from '@mui/icons-material';

import { useDispatch, useSelector } from '../../store/utils';
import { variablesChange } from '../../store/actions';
import { useFormatNumber } from '../../utils/format-number';

import { DialogClose } from '../../atoms/dialog-close';
import { ParentPaper } from '../../atoms/parent-paper';

type Props = {
  open: boolean;
  onClose: () => void;
  variant?: 'fullscreen' | 'dialog';
};

type Step = 'acknowledge' | 'running' | 'result';

// Actual consumption of the building — mirrors getHomeActualPower() in
// core/src/modules/heat_water/boiler-test.ts.
const useHomeActualPower = () =>
  useSelector((s) => {
    const v = (path: string) => Number(get(s.variables, path)) || 0;

    return (
      v('PV_ACTUAL_POWER_BATTERY') +
      (v('WIND_POWER') - v('WIND_RESISTOR_POWER')) -
      v('WB_ACTUAL_POWER') -
      v('HP_ACTUAL_POWER') +
      v('PV_ACTUAL_POWER') +
      v('EM_INFO.Consumption[0]') +
      v('EM_INFO.Consumption[1]') +
      v('EM_INFO.Consumption[2]')
    );
  });

export const BoilerTestWizard = ({
  open,
  onClose,
  variant = 'fullscreen',
}: Props) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState<Step>('acknowledge');

  const inProgress = useSelector((s) => s.variables.HW_SSR_TEST_IN_PROGRESS);
  const phase3 = useSelector((s) => s.variables.PHASE_3_HW);
  const hwPhase = useSelector((s) => s.variables.HW_PHASE);
  const info = useSelector((s) => s.variables.HW_SSR_TEST_INFO);

  // Which phases the current run covers, read back from the results the core
  // writes — so every browser window renders the same run, whoever started it.
  const testedPhases = (phase3 ? [0, 1, 2] : [hwPhase]).filter(
    (i) => info[i] && !info[i].skipped
  );

  // On open, jump straight to the running step if a test is already in
  // progress (e.g. another user started it); otherwise start from the top.
  // Keyed on `open` only — later inProgress transitions are handled below.
  useEffect(() => {
    if (!open) return;
    setStep(inProgress ? 'running' : 'acknowledge');
  }, [open]);

  // When the test finishes on the server, advance to the result step.
  const prevInProgress = useRef(false);
  useEffect(() => {
    if (prevInProgress.current && !inProgress && step === 'running') {
      setStep('result');
    }
    prevInProgress.current = inProgress;
  }, [inProgress, step]);

  const handleStart = (phases: boolean[]) => {
    // Clear any stale results from a previous run before starting.
    dispatch(
      variablesChange({
        HW_SSR_TEST_PHASE: phase3 ? phases : [true, true, true],
        HW_SSR_TEST_INFO: [],
        HW_SSR_TEST: true,
      })
    );
    setStep('running');
  };

  const progress =
    step === 'acknowledge' ? 100 / 3 : step === 'running' ? 200 / 3 : 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={variant === 'fullscreen'}
      slotProps={{ paper: { sx: { bgcolor: 'secondary.main' } } }}
      fullWidth
      maxWidth={variant === 'fullscreen' ? false : 'sm'}
    >
      <DialogClose onClose={onClose} />

      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Typography variant="h3">
          <FormattedMessage
            id="boiler_test_wizard.title"
            defaultMessage="Testování bojleru"
          />
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 2 }}>
        <LinearProgress variant="determinate" value={progress} />
      </Box>

      <DialogContent
        sx={{ display: 'flex', justifyContent: 'center', p: 0, py: 3 }}
      >
        <Container sx={{ display: 'flex', flexDirection: 'column' }}>
          {step === 'acknowledge' && <AcknowledgeStep onStart={handleStart} />}
          {step === 'running' && <RunningStep phases={testedPhases} />}
          {step === 'result' && (
            <ResultStep
              phases={testedPhases}
              onClose={onClose}
              // Re-run exactly the phases the finished run covered.
              onRerun={() =>
                handleStart([0, 1, 2].map((i) => testedPhases.includes(i)))
              }
            />
          )}
        </Container>
      </DialogContent>
    </Dialog>
  );
};

// Step 1: technician acknowledges the test conditions and the configuration.
const AcknowledgeStep = ({
  onStart,
}: {
  onStart: (phases: boolean[]) => void;
}) => {
  const formatNumber = useFormatNumber();

  const [phase3, nominal] = useSelector(
    (s) => [s.variables.PHASE_3_HW, s.variables.HW_EK_NOMINAL] as const
  );
  const phaseCount = phase3 ? 3 : 1;
  const totalPower = nominal * phaseCount;
  const homeActualPower = useHomeActualPower();

  const [conditionConfirmed, setConditionConfirmed] = useState(false);
  const [configConfirmed, setConfigConfirmed] = useState(false);
  // The step is remounted every time the wizard opens, so every phase starts
  // selected.
  const [phases, setPhases] = useState<boolean[]>([true, true, true]);

  const togglePhase = (index: number) =>
    setPhases((prev) => prev.map((v, i) => (i === index ? !v : v)));

  const anyPhaseSelected = !phase3 || phases.some(Boolean);
  const canStart = conditionConfirmed && configConfirmed && anyPhaseSelected;

  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="boiler_test_wizard.acknowledge.title"
          defaultMessage="Informace k testu"
        />
      </Typography>

      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="boiler_test_wizard.acknowledge.condition"
              defaultMessage="Pro správný test bojleru je nutné, aby byla spotřeba v objektu konstantní a nebo nulová. Jakékoliv výkyvy ve spotřebě mohou test ovlivnit. Aktuální spotřeba objektu je {power}"
              values={{
                power: (
                  <strong>
                    {formatNumber(homeActualPower, {
                      style: 'unit',
                      unit: 'kilowatt',
                      maximumFractionDigits: 1,
                    })}
                  </strong>
                ),
              }}
            />
          </Typography>
        </Paper>
        <ConfirmRow
          checked={conditionConfirmed}
          onToggle={() => setConditionConfirmed((v) => !v)}
        />
      </ParentPaper>

      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="boiler_test_wizard.acknowledge.config"
              defaultMessage="Aktuálně je nastaven {phaseLabel} bojler o celkové výkonu {power}. Tyto hodnoty musí být zadané dle štítku patrony."
              values={{
                phaseLabel: (
                  <strong>
                    {phase3 ? (
                      <FormattedMessage
                        id="boiler_test_wizard.acknowledge.phase3"
                        defaultMessage="3-fázový"
                      />
                    ) : (
                      <FormattedMessage
                        id="boiler_test_wizard.acknowledge.phase1"
                        defaultMessage="1-fázový"
                      />
                    )}
                  </strong>
                ),
                power: (
                  <strong>
                    {formatNumber(totalPower, {
                      style: 'unit',
                      unit: 'kilowatt',
                      maximumFractionDigits: 1,
                    })}
                  </strong>
                ),
              }}
            />
          </Typography>
        </Paper>
        <ConfirmRow
          checked={configConfirmed}
          onToggle={() => setConfigConfirmed((v) => !v)}
        />
      </ParentPaper>

      {phase3 && (
        <ParentPaper>
          <Paper>
            <Typography variant="body1">
              <FormattedMessage
                id="boiler_test_wizard.acknowledge.phase_select"
                defaultMessage="Vyberte fáze, které se mají otestovat. Neoznačené fáze budou přeskočeny."
              />
            </Typography>
          </Paper>
          {[0, 1, 2].map((index) => (
            <PhaseRow
              key={index}
              phase={index}
              checked={phases[index]}
              onToggle={() => togglePhase(index)}
            />
          ))}
          {!anyPhaseSelected && (
            <Paper sx={{ pt: 0 }}>
              <Typography variant="body2" color="error">
                <FormattedMessage
                  id="boiler_test_wizard.acknowledge.phase_select_empty"
                  defaultMessage="Vyberte alespoň jednu fázi."
                />
              </Typography>
            </Paper>
          )}
        </ParentPaper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 2 }}>
        <Button
          variant="contained"
          disabled={!canStart}
          onClick={() => onStart(phases)}
        >
          <FormattedMessage
            id="boiler_test_wizard.acknowledge.start"
            defaultMessage="Otestovat bojler"
          />
        </Button>
      </Box>
    </Stack>
  );
};

const ConfirmRow = ({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) => (
  <Paper
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      pt: 0,
      pb: 1,
    }}
  >
    <Typography variant="body1" sx={{ opacity: 0.7 }}>
      <FormattedMessage
        id="boiler_test_wizard.acknowledge.confirm"
        defaultMessage="Potvrzuji"
      />
    </Typography>
    <Checkbox checked={checked} readOnly size="large" />
  </Paper>
);

const PhaseRow = ({
  phase,
  checked,
  onToggle,
}: {
  phase: number;
  checked: boolean;
  onToggle: () => void;
}) => (
  // The Paper itself must stay a block element — ParentPaper injects an
  // `::after` divider that would otherwise become a flex item and push the
  // checkbox towards the middle of the row.
  <Paper onClick={onToggle} sx={{ cursor: 'pointer', pt: 0, pb: 1 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="body1" sx={{ opacity: 0.7 }}>
        <FormattedMessage
          id="boiler_test_wizard.acknowledge.phase_option"
          defaultMessage="{phase}. fáze"
          values={{ phase: phase + 1 }}
        />
      </Typography>
      <Checkbox checked={checked} readOnly size="large" />
    </Box>
  </Paper>
);

// Step 2: live log of the running test, one card per phase.
const RunningStep = ({ phases }: { phases: number[] }) => {
  const info = useSelector((s) => s.variables.HW_SSR_TEST_INFO);
  const remaining = useSelector((s) => s.variables.HW_SSR_TEST_REMAINING_TIME);

  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="boiler_test_wizard.running.title"
          defaultMessage="Probíhá test"
        />
      </Typography>

      <ParentPaper>
        <Paper>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1">
              <FormattedMessage
                id="boiler_test_wizard.running.remaining"
                defaultMessage="Zbývá přibližně {seconds} s"
                values={{ seconds: remaining }}
              />
            </Typography>
          </Box>
        </Paper>
      </ParentPaper>

      {phases.map((phase) => (
        <PhaseLog key={phase} phase={phase} entry={info[phase]} />
      ))}
    </Stack>
  );
};

type TestInfo = {
  at: string;
  phase: number;
  inProgress: boolean;
  temperature: number;
  setPower: number;
  homeConsumption: number;
  homeConsumptionNext: number;
  boilerRealPower: number;
  result: boolean;
  missingPower: number;
  excessPower: number;
  ssrPower: number;
  measurementUnstable: boolean;
  skipped: boolean;
};

// A single line of the live phase log, with a status icon on the left.
const StepLine = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ display: 'flex', width: 20, justifyContent: 'center' }}>
      {icon}
    </Box>
    <Typography variant="body1" sx={{ opacity: 0.85 }}>
      {children}
    </Typography>
  </Box>
);

const doneIcon = <CheckCircle color="success" fontSize="small" />;

const PhaseLog = ({ phase, entry }: { phase: number; entry?: TestInfo }) => {
  const formatNumber = useFormatNumber();

  // Nothing logged yet for this phase.
  if (!entry) return null;

  const kw = (value: number) =>
    formatNumber(value, {
      style: 'unit',
      unit: 'kilowatt',
      maximumFractionDigits: 1,
    });

  // Derive how far the phase has progressed from the fields populated so far.
  const baselineDone = entry.homeConsumption > 0;
  const activationDone =
    entry.ssrPower === 100 || entry.homeConsumptionNext > 0;
  const activeMeasureDone = entry.homeConsumptionNext > 0;
  const completed = !entry.inProgress;

  const lines: React.ReactNode[] = [];

  // 1. Reset consumption.
  lines.push(
    <StepLine key="reset" icon={doneIcon}>
      <FormattedMessage
        id="boiler_test_wizard.running.reset"
        defaultMessage="Nulování spotřeby"
      />
    </StepLine>
  );

  // 2. Baseline consumption.
  if (baselineDone) {
    lines.push(
      <StepLine key="before" icon={doneIcon}>
        <FormattedMessage
          id="boiler_test_wizard.running.before"
          defaultMessage="Spotřeba objektu před testem je {power}"
          values={{ power: kw(entry.homeConsumption) }}
        />
      </StepLine>
    );
  }

  // 3. Activate the boiler phase.
  if (activationDone) {
    lines.push(
      <StepLine key="activate" icon={doneIcon}>
        <FormattedMessage
          id="boiler_test_wizard.running.activate"
          defaultMessage="Aktivace fáze bojleru"
        />
      </StepLine>
    );
  }

  // 4. Consumption with the boiler running.
  if (activeMeasureDone) {
    lines.push(
      <StepLine key="after" icon={doneIcon}>
        <FormattedMessage
          id="boiler_test_wizard.running.after"
          defaultMessage="Spotřeba objektu je {power}"
          values={{ power: kw(entry.homeConsumptionNext) }}
        />
      </StepLine>
    );
  }

  // 5-7. Evaluation, shown once the phase has finished.
  if (completed) {
    lines.push(
      <StepLine
        key="stable"
        icon={
          entry.measurementUnstable ? (
            <Cancel color="error" fontSize="small" />
          ) : (
            doneIcon
          )
        }
      >
        {entry.measurementUnstable ? (
          <FormattedMessage
            id="boiler_test_wizard.running.unstable"
            defaultMessage="Měření nebylo stabilní"
          />
        ) : (
          <FormattedMessage
            id="boiler_test_wizard.running.stable"
            defaultMessage="Spotřeba je stabilní"
          />
        )}
      </StepLine>
    );
    lines.push(
      <StepLine key="real" icon={doneIcon}>
        <FormattedMessage
          id="boiler_test_wizard.running.real_power"
          defaultMessage="Odhadovaný výkon bojleru je {power}"
          values={{ power: kw(entry.boilerRealPower) }}
        />
      </StepLine>
    );
    lines.push(
      <StepLine
        key="result"
        icon={
          entry.result ? doneIcon : <Cancel color="error" fontSize="small" />
        }
      >
        {entry.result ? (
          <FormattedMessage
            id="boiler_test_wizard.running.result_ok"
            defaultMessage="Test fáze je korektní"
          />
        ) : (
          <FormattedMessage
            id="boiler_test_wizard.running.result_fail"
            defaultMessage="Test fáze není korektní"
          />
        )}
      </StepLine>
    );
  } else {
    // Still running — show that a measurement is in progress.
    lines.push(
      <StepLine key="measuring" icon={<CircularProgress size={16} />}>
        <FormattedMessage
          id="boiler_test_wizard.running.measuring"
          defaultMessage="Probíhá měření…"
        />
      </StepLine>
    );
  }

  return (
    <ParentPaper>
      <Paper>
        <Stack gap={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h3">
              <FormattedMessage
                id="boiler_test_wizard.running.phase"
                defaultMessage="Testuji fázi {phase}"
                values={{ phase: phase + 1 }}
              />
            </Typography>
            {entry.inProgress ? (
              <CircularProgress size={16} />
            ) : entry.result ? (
              <CheckCircle color="success" fontSize="small" />
            ) : (
              <Cancel color="error" fontSize="small" />
            )}
          </Box>
          {lines}
        </Stack>
      </Paper>
    </ParentPaper>
  );
};

// Step 3: result table.
const ResultStep = ({
  phases: phaseIndexes,
  onClose,
  onRerun,
}: {
  phases: number[];
  onClose: () => void;
  onRerun: () => void;
}) => {
  const formatNumber = useFormatNumber();
  const info = useSelector((s) => s.variables.HW_SSR_TEST_INFO);

  const [showDetail, setShowDetail] = useState(false);

  const phases = phaseIndexes.map((i) => info[i]).filter(Boolean);
  const passed = phases.length > 0 && phases.every((p) => p.result);

  // Fixed value-column width so the detail and summary grids stay aligned.
  const gridColumns = `minmax(0, 1fr) repeat(${phases.length}, 64px)`;

  const kw = (value: number) =>
    formatNumber(value, {
      style: 'unit',
      unit: 'kilowatt',
      maximumFractionDigits: 1,
    });

  const boolIcon = (value: boolean) =>
    value ? (
      <CheckCircle color="success" fontSize="small" />
    ) : (
      <Cancel color="error" fontSize="small" />
    );

  const rows: { label: React.ReactNode; values: React.ReactNode[] }[] = [
    {
      label: (
        <FormattedMessage
          id="boiler_test_wizard.result.set_power"
          defaultMessage="Zadaný výkon"
        />
      ),
      values: phases.map((p) => kw(p.setPower)),
    },
    {
      label: (
        <FormattedMessage
          id="boiler_test_wizard.result.before"
          defaultMessage="Spotřeba objektu před aktivací"
        />
      ),
      values: phases.map((p) => kw(p.homeConsumption)),
    },
    {
      label: (
        <FormattedMessage
          id="boiler_test_wizard.result.after"
          defaultMessage="Spotřeba objektu po aktivaci"
        />
      ),
      values: phases.map((p) => kw(p.homeConsumptionNext)),
    },
    {
      label: (
        <FormattedMessage
          id="boiler_test_wizard.result.stable"
          defaultMessage="Měření bylo stabilní"
        />
      ),
      values: phases.map((p) => boolIcon(!p.measurementUnstable)),
    },
    {
      label: (
        <FormattedMessage
          id="boiler_test_wizard.result.correct"
          defaultMessage="Spotřeba bojleru korektní"
        />
      ),
      values: phases.map((p) => boolIcon(p.result)),
    },
    {
      label: (
        <FormattedMessage
          id="boiler_test_wizard.result.real_power"
          defaultMessage="Odhadovaný příkon bojleru"
        />
      ),
      values: phases.map((p) => kw(p.boilerRealPower)),
    },
  ];

  // Show the two summary rows by default; the rest is behind the collapse.
  const detailRows = rows.slice(0, rows.length - 2);
  const summaryRows = rows.slice(rows.length - 2);

  const phaseHeaderCells = (
    <>
      <Box />
      {phases.map((_, i) => (
        <Typography key={`h-${i}`} variant="h5" sx={{ textAlign: 'right' }}>
          <FormattedMessage
            id="boiler_test_wizard.result.phase_header"
            defaultMessage="{phase}. fáze"
            values={{ phase: phaseIndexes[i] + 1 }}
          />
        </Typography>
      ))}
    </>
  );

  const renderRow = (row: typeof rows[number], ri: number) => (
    <React.Fragment key={ri}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {row.label}
      </Typography>
      {row.values.map((value, ci) => (
        <Box
          key={ci}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {typeof value === 'string' ? (
            <Typography variant="body1">{value}</Typography>
          ) : (
            value
          )}
        </Box>
      ))}
    </React.Fragment>
  );

  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="boiler_test_wizard.result.title"
          defaultMessage="Výsledek testu"
        />
      </Typography>

      <ParentPaper>
        <Paper>
          <Stack gap={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h3">
                <FormattedMessage
                  id="boiler_test_wizard.result.header"
                  defaultMessage="Výsledek testu:"
                />
              </Typography>
              {passed ? (
                <CheckCircle color="success" />
              ) : (
                <Cancel color="error" />
              )}
            </Box>

            {/* Always-visible summary rows */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                rowGap: 1.5,
                columnGap: 1,
                alignItems: 'center',
              }}
            >
              {phaseHeaderCells}
              {summaryRows.map(renderRow)}
            </Box>

            {/* Toggle for the detailed measurements */}
            <Button
              variant="text"
              size="small"
              onClick={() => setShowDetail((v) => !v)}
              endIcon={
                <ExpandMore
                  sx={{
                    transition: 'transform 0.2s',
                    transform: showDetail ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              <FormattedMessage
                id="boiler_test_wizard.result.detail_toggle"
                defaultMessage="Detail testu"
              />
            </Button>

            <Collapse in={showDetail} unmountOnExit>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridColumns,
                  rowGap: 1.5,
                  columnGap: 1,
                  alignItems: 'center',
                }}
              >
                {detailRows.map(renderRow)}
              </Box>
            </Collapse>
          </Stack>
        </Paper>
      </ParentPaper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, py: 2 }}>
        <Button variant="outlined" onClick={onRerun}>
          <FormattedMessage
            id="boiler_test_wizard.result.rerun"
            defaultMessage="Spustit test znovu"
          />
        </Button>
        <Button variant="contained" onClick={onClose}>
          <FormattedMessage
            id="boiler_test_wizard.result.close"
            defaultMessage="Zavřít"
          />
        </Button>
      </Box>
    </Stack>
  );
};
